import { compareScrubSizes, parseScrubSize } from './scrub-sizing.js';
import { loadComponentAvailability } from './performance-set-components.js';
import { colorAvailability, colorPresentation, normalizeColor, packState, quantitiesMatch, resolveSelection, variantState } from './performance-set-selection.js';

const ROOT_SELECTOR = '.product-info.performance-set-v2[data-performance-set-product-id="15787439161692"][data-performance-set-copy]';
const WIDGET_SELECTOR = 'kaching-bundles-block, kaching-bundle, .kaching-bundles__block';
const DEAL_BAR_SELECTOR = '.kaching-bundles__bar';
const SELECTOR_ROW = '.kaching-bundles__bar-variant';
const SELECTOR_SELECT = '.kaching-bundles__bar-variant-select';
const SELECTOR_SELECTED_BAR = '.kaching-bundles__bar--selected';
// Shopify widget events: https://support.kachingappz.com/en/articles/8609400
// Its events and DOM updates drive inspect(), which hides the loader only once
// the deal bars are rendered. The headless SDK's `loaded` event is a different API.
const KACHING_EVENTS = ['deal-bar-selected', 'variant-selected', 'variants-changed'];
const FALLBACK_DELAY = 10000;
const EMPTY_WIDGET_GRACE = 1500;
const VARIANT_RESOLVE_TIMEOUT = 10000;

const controllers = new WeakMap();

class PerformanceSetKachingConnector {
  constructor(root, copy) {
    this.root = root;
    this.copy = copy;
    this.abortController = new AbortController();
    this.boundWidgets = new WeakSet();
    this.boundSubmitButtons = new WeakSet();
    this.originalHeadlinePrices = new WeakMap();
    this.inspectTimer = null;
    this.emptyTimer = null;
    this.variantTimer = null;
    this.fallbackTimer = null;
    this.packAnchorFrame = null;
    this.mutationObserver = new MutationObserver(() => this.onRootMutation());
    this.fakeLowStockCount = this.readFakeLowStockCount();
    this.coreColors = this.readPromotionalColors('coreColors');
    this.limitedColors = this.readPromotionalColors('limitedColors');
    this.petiteColors = this.readColorList('performanceSetPetiteColors');
    this.pendingAdjustments = new Map();
    this.variantQuantities = null;
    this.resolvingSelection = false;
    this.adjustingSelection = false;
    this.nativePending = null;
    this.productCache = null;
    this.componentAvailability = new Map();
    this.inventoryRequests = new Map();
    this.inventoryErrors = new Set();
    const inventory = root.querySelector('[data-performance-set-inventory]');
    this.inventoryUrl = inventory?.dataset.productUrl;
    try {
      this.preorderInventory = JSON.parse(inventory?.textContent || '{}');
    } catch (_error) {
      this.preorderInventory = {};
    }
    this.galleryColor = normalizeColor(this.getNativeVariant()?.options?.[0]);
    this.galleryRequestId = 0;
    try {
      this.swatchBackgrounds = JSON.parse(this.root.querySelector('[data-performance-set-swatches]')?.textContent || '{}');
    } catch (_error) {
      this.swatchBackgrounds = {};
    }
  }

  connect() {
    if (this.root.dataset.kachingConnectorInitialized === 'true') return;

    this.root.dataset.kachingConnectorInitialized = 'true';
    this.root.dataset.kachingState = 'loading';
    if (this.getLoadingGraceDelay() > 0) this.root.classList.add('is-kaching-loading');
    this.mutationObserver.observe(this.root, {
      childList: true, characterData: true, subtree: true,
      attributes: true, attributeFilter: ['product', 'disabled', 'aria-busy'],
    });

    this.root.addEventListener('change', (event) => this.onOptionChange(event), {
      signal: this.abortController.signal,
    });
    this.root.addEventListener('change', (event) => this.onBeforeOptionChange(event), {
      capture: true,
      signal: this.abortController.signal,
    });
    this.root.addEventListener('click', (event) => {
      this.anchorPackExpansion(event);
      const button = event.target.closest('.performance-set-kaching__image-preview');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const thumbnail = button.parentElement.querySelector('.kaching-bundles__bar-variant-image');
      if (!thumbnail?.src) return;
      // Keep the clicked colour even if the widget updates while the module loads.
      const source = thumbnail.src;
      const row = button.closest(SELECTOR_ROW);
      const label = `Preview ${this.getRowSelects(row).color?.value || 'product'} image`;
      import('kaching-image-preview').then(({ openImagePreview }) => {
        if (button.isConnected) openImagePreview(source, label);
      });
    }, { capture: true, signal: this.abortController.signal });
    // Give control back immediately when the customer scrolls or navigates.
    for (const type of ['wheel', 'touchmove', 'keydown']) {
      window.addEventListener(type, () => this.cancelPackAnchor(), {
        passive: true, signal: this.abortController.signal,
      });
    }
    this.root.addEventListener('submit', (event) => {
      if (event.target !== this.getProductForm() || !this.purchaseNeedsResolution()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.renderSelectionNotice(this.selectionMessage());
    }, { capture: true, signal: this.abortController.signal });
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button[type="submit"]');
      if (!button || button.form !== this.getProductForm() || !this.purchaseNeedsResolution()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.renderSelectionNotice(this.selectionMessage());
    }, { capture: true, signal: this.abortController.signal });
    this.root.addEventListener('variant:change', (event) => this.onVariantResolved(event), {
      signal: this.abortController.signal,
    });

    this.fallbackTimer = window.setTimeout(() => {
      if (!this.isReady()) this.showFallback(this.text('fallback'));
    }, this.getFallbackDelay());

    this.inspect();
  }

  destroy() {
    this.cancelPackAnchor();
    this.restoreHeadlinePrice();
    this.abortController.abort();
    this.mutationObserver.disconnect();
    window.clearTimeout(this.inspectTimer);
    window.clearTimeout(this.emptyTimer);
    window.clearTimeout(this.variantTimer);
    window.clearTimeout(this.fallbackTimer);
    this.root.classList.remove('is-kaching-loading');
    this.root.removeAttribute('data-kaching-connector-initialized');
  }

  cancelPackAnchor() {
    window.cancelAnimationFrame(this.packAnchorFrame);
    this.packAnchorFrame = null;
    this.root.classList.remove('is-anchoring-pack');
  }

  anchorPackExpansion(event) {
    const trigger = event.target.closest('.kaching-bundles__bar-main, .kaching-bundles__bar > input[type="radio"]');
    const bar = trigger?.closest(DEAL_BAR_SELECTOR);
    if (!bar || bar.matches(SELECTOR_SELECTED_BAR)) return;

    this.cancelPackAnchor();
    const top = bar.getBoundingClientRect().top;
    const started = performance.now();
    this.root.classList.add('is-anchoring-pack');
    const keepHeaderAtTop = () => {
      if (!bar.isConnected) { this.cancelPackAnchor(); return; }
      const delta = bar.getBoundingClientRect().top - top;
      if (Math.abs(delta) > .5) window.scrollBy({ top: delta, behavior: 'instant' });
      if (performance.now() - started < 420) {
        this.packAnchorFrame = window.requestAnimationFrame(keepHeaderAtTop);
      } else {
        this.cancelPackAnchor();
      }
    };
    this.packAnchorFrame = window.requestAnimationFrame(keepHeaderAtTop);
  }

  onRootMutation() {
    if (this.root.querySelector(DEAL_BAR_SELECTOR)) {
      this.syncHeadlinePrice();
    }
    // A widget or section rerender may replace or re-enable the submit button.
    // Recompute its current state; restoring an earlier disabled flag is unsafe.
    this.updatePurchaseGuard();
    this.scheduleInspection();
  }

  scheduleInspection() {
    window.clearTimeout(this.inspectTimer);
    this.inspectTimer = window.setTimeout(() => this.inspect(), 100);
  }

  inspect() {
    if (!this.root.isConnected) {
      this.destroy();
      return;
    }

    const widgets = Array.from(this.root.querySelectorAll(WIDGET_SELECTOR));
    widgets.forEach((widget) => this.bindWidget(widget));

    const dealBars = Array.from(this.root.querySelectorAll(DEAL_BAR_SELECTOR));
    if (dealBars.length > 0) {
      window.clearTimeout(this.emptyTimer);
      this.emptyTimer = null;
      this.decorateDealBars(dealBars);
      this.syncHeadlinePrice();
      this.markReady(dealBars[0]);
      this.resolveAdjustments();
      this.updatePurchaseGuard();
      return;
    }

    this.updatePurchaseGuard();

    if (this.isReady() && !this.emptyTimer) {
      this.emptyTimer = window.setTimeout(() => {
        this.emptyTimer = null;
        if (!this.root.querySelector(DEAL_BAR_SELECTOR)) {
          this.showFallback(this.text('fallback'));
        }
      }, EMPTY_WIDGET_GRACE);
    }
  }

  bindWidget(widget) {
    if (widget.matches('kaching-bundles-block')) this.disableVariantOptionSync(widget);
    if (this.boundWidgets.has(widget)) return;

    this.boundWidgets.add(widget);
    widget.closest('[data-block-type="@app"]')?.setAttribute('data-kaching-connector-block', 'true');

    for (const eventName of KACHING_EVENTS) {
      widget.addEventListener(eventName, (event) => this.onKachingEvent(event), {
        signal: this.abortController.signal,
      });
    }
  }

  disableVariantOptionSync(widget) {
    const serializedDeal = widget.getAttribute('deal-block');
    if (!serializedDeal) return;

    let deal;
    try {
      deal = JSON.parse(serializedDeal);
    } catch (_error) {
      return;
    }

    if (deal.disableVariantOptionSync === true) {
      this.root.dataset.kachingVariantOptionSync = 'disabled';
      return;
    }

    deal.disableVariantOptionSync = true;
    widget.setAttribute('deal-block', JSON.stringify(deal));
    this.root.dataset.kachingVariantOptionSync = 'disabled';
  }

  onKachingEvent(event) {
    this.root.dataset.kachingLastEvent = event.type;

    const selectedQuantity = event.detail?.quantity ?? event.detail?.selectedQuantity;
    if (selectedQuantity != null) this.root.dataset.kachingSelectedQuantity = String(selectedQuantity);

    if (event.type === 'variant-selected') {
      const variantId = String(event.detail?.variantId ?? '').split('/').pop();
      const variant = this.getWidgetProduct()?.variants?.find((item) => String(item.id).split('/').pop() === variantId);
      this.syncGalleryColor(variant?.options?.[0], variant);
    }

    if (event.type === 'deal-bar-selected') {
      this.pendingAdjustments.clear();
      this.removeSelectionNotice();
      this.resolvingSelection = true;
    }
    if (event.type === 'variants-changed') {
      this.variantQuantities = event.detail?.variantIdQuantities ?? null;
      this.resolvingSelection = false;
    }

    this.updatePurchaseGuard();
    this.scheduleInspection();
  }

  markReady(dealBar) {
    window.clearTimeout(this.fallbackTimer);
    window.clearTimeout(this.variantTimer);

    this.root.classList.remove('is-kaching-loading');
    this.root.classList.add('is-kaching-ready');
    this.root.dataset.kachingState = 'ready';
    dealBar.closest('[data-block-type="@app"]')?.setAttribute('data-kaching-connector-block', 'true');
    this.removeStatusMessage();
  }

  showFallback(message) {
    this.root.classList.remove('is-kaching-loading', 'is-kaching-ready', 'is-kaching-presented');
    this.root.dataset.kachingState = 'fallback';
    this.pendingAdjustments.clear();
    this.resolvingSelection = false;
    this.restoreHeadlinePrice();
    this.renderStatusMessage(message);
    this.updatePurchaseGuard();
  }

  isReady() {
    return this.root.classList.contains('is-kaching-ready');
  }

  onOptionChange(event) {
    if (event.target.matches(SELECTOR_SELECT)) {
      this.updatePurchaseGuard();
      this.scheduleInspection();
      return;
    }

    if (this.isReady() || !event.target.matches('.product-info__variant-picker input[data-option-position]')) return;

    const form = this.getProductForm();
    if (!form) return;

    form.setAttribute('aria-busy', 'true');
    this.updatePurchaseGuard();

    window.clearTimeout(this.variantTimer);
    this.variantTimer = window.setTimeout(() => {
      if (!this.root.isConnected || this.isReady()) return;
      this.renderStatusMessage(this.text('combinationError'));
    }, VARIANT_RESOLVE_TIMEOUT);
  }

  onVariantResolved(event) {
    if (this.nativePending) {
      const expected = this.nativePending;
      const resolved = event.detail?.variant;
      const picker = this.root.querySelector('variant-picker');
      const selectedIds = Array.from(picker?.querySelectorAll('input[data-option-position]:checked') || [])
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map((input) => input.value);
      const invalidLatest = !resolved?.id && selectedIds.length === expected.ids.length && selectedIds.every((id, index) => id === expected.ids[index]);
      if (invalidLatest || (resolved?.options?.length === expected.values.length && resolved.options.every((value, index) => value === expected.values[index]))) {
        this.nativePending = null;
      } else {
        // The native picker can receive an older section response after a newer choice.
        // Resolve the latest requested IDs once more instead of enabling a stale variant.
        if (!expected.retried) {
          expected.retried = true;
          this.root.querySelector('variant-picker')?.selectCombination({ optionValues: expected.ids, productChange: false })
            .catch(() => this.renderSelectionNotice(this.text('sizesError')));
        }
        this.updatePurchaseGuard();
        return;
      }
    }
    window.clearTimeout(this.variantTimer);
    const form = this.getProductForm();
    form?.removeAttribute('aria-busy');

    if (this.root.dataset.kachingState !== 'fallback') this.removeStatusMessage();
    if (this.isReady()) {
      this.scheduleInspection();
    }
    this.updatePurchaseGuard();
  }

  parseSize(value) {
    return parseScrubSize(value);
  }

  supportsPetite(color) {
    return this.petiteColors.has(String(color).toLowerCase());
  }

  getRowSelects(row) {
    const fields = {};
    row.querySelectorAll(SELECTOR_SELECT).forEach((select, index) => {
      fields[this.getFieldType(select, index)] = select;
    });
    return fields;
  }

  getWidgetProduct() {
    const serialized = this.root.querySelector('kaching-bundles-block')?.getAttribute('product');
    if (!serialized) return null;
    if (serialized !== this.productCache?.serialized) {
      try {
        this.productCache = { serialized, product: JSON.parse(serialized) };
      } catch (_error) {
        return null;
      }
    }
    return this.productCache.product;
  }

  getComponentAvailability(color, request = false) {
    const entry = this.componentAvailability.get(color);
    if (entry?.pending || (entry && Date.now() < entry.expires)) return entry?.data;
    const variants = this.getWidgetProduct()?.variants;
    if (!request || !color || !variants?.length) return entry?.data;
    const next = { pending: true, data: entry?.data };
    this.componentAvailability.set(color, next);
    loadComponentAvailability(variants, color, window.Shopify?.country || 'GB')
      .then(data => { next.data = data; next.expires = Date.now() + 60000; })
      .catch(() => { next.expires = Date.now() + 10000; })
      .finally(() => {
        next.pending = false;
        if (this.root.isConnected && !this.abortController.signal.aborted) this.scheduleInspection();
      });
    return next.data;
  }

  getNativeVariant() {
    const picker = this.root.querySelector('variant-picker');
    if (picker?.selectedVariant !== undefined) return picker.selectedVariant;
    try {
      return JSON.parse(picker?.querySelector('script[data-variant]')?.textContent || 'null');
    } catch (_error) {
      return null;
    }
  }

  syncGalleryColor(color, selectedVariant = null) {
    const gallery = this.root.closest('.product')?.querySelector('product-gallery');
    // Desktop grids keep their page position; carousels follow the chosen colour.
    const isDesktopGrid = () => gallery?.classList.contains('product-gallery--desktop-grid') && window.matchMedia('(min-width: 1000px)').matches;
    if (isDesktopGrid()) {
      this.galleryRequestId++;
      return;
    }
    const normalized = normalizeColor(color);
    // Kaching emits initialization and size events too. Only a new colour moves
    // the gallery; aggregate bundle events must not reset it to the first row.
    if (!normalized || normalized === this.galleryColor) return;
    const carousel = gallery?.querySelector('media-carousel');
    if (!carousel) return;

    const variants = this.getWidgetProduct()?.variants || [];
    const variant = selectedVariant?.imageId ? selectedVariant : variants.find((item) => (
      normalizeColor(item.options?.[0]) === normalized && item.imageId
    ));
    if (!variant?.imageId) return;
    // Kaching image IDs and Shopify media IDs are different identifiers.
    const target = Array.from(carousel.querySelectorAll('[data-image-id]')).find((item) => (
      item.dataset.imageId === String(variant.imageId).split('/').pop()
    ));
    if (!target) return;

    this.galleryColor = normalized;
    const requestId = ++this.galleryRequestId;
    const selectImage = () => {
      if (!this.root.isConnected || !carousel.isConnected || requestId !== this.galleryRequestId || isDesktopGrid()) return;
      const index = Array.from(carousel.children).indexOf(target);
      if (index < 0) return;
      target.hidden = false;
      const image = target.querySelector('img');
      if (image) image.loading = 'eager';
      carousel.select(index, { animate: false, force: true });
    };
    if (typeof carousel.select === 'function') selectImage();
    else window.customElements.whenDefined('media-carousel').then(selectImage);
  }

  onBeforeOptionChange(event) {
    const input = event.target;
    if (input.matches(SELECTOR_SELECT)) {
      this.resolvingSelection = true;
      const row = input.closest(SELECTOR_ROW);
      const bar = input.closest(DEAL_BAR_SELECTOR);
      const rows = Array.from(bar.querySelectorAll(SELECTOR_ROW));
      const rowIndex = rows.indexOf(row);
      const key = `${bar.dataset.dealBarId}:${rowIndex}`;
      if (!this.adjustingSelection) {
        this.pendingAdjustments.delete(key);
        this.removeSelectionNotice();
        const fields = this.getRowSelects(row);
        // Also cover unavailable size combinations for which Kaching cannot
        // emit a resolved variant ID. Use another variant's image of that colour.
        if (input === fields.color) this.syncGalleryColor(input.value);
        const jogger = this.parseSize(fields.bottom?.value);
        if (input === fields.color && jogger?.fit === 'petite' && !this.supportsPetite(input.value)) {
          const regular = this.getWidgetProduct()?.options?.[2]?.optionValues.find((option) => {
            const size = this.parseSize(option.name);
            return size?.size === jogger.size && size.fit === 'regular';
          });
          if (regular) {
            this.pendingAdjustments.set(key, {
              barId: bar.dataset.dealBarId, rowIndex, startedAt: Date.now(),
              values: [input.value, fields.top.value, regular.name], size: jogger.size,
            });
          }
        }
      }
      this.updatePurchaseGuard();
      this.scheduleInspection();
      return;
    }

    if (this.isReady() || !input.matches('input[data-option-position]')) return;
    const picker = input.closest('variant-picker');
    const serialized = picker?.querySelector('[data-performance-set-options]')?.textContent;
    if (!serialized) return;
    let options;
    try {
      options = JSON.parse(serialized);
    } catch (_error) {
      return;
    }
    const checked = () => options.map((option) => {
      const radio = picker.querySelector(`input[data-option-position="${option.position}"]:checked`);
      return { radio, value: option.values.find((value) => String(value.id) === radio?.value) };
    });
    let values = checked();
    const jogger = this.parseSize(values[2]?.value?.name);
    this.removeSelectionNotice();
    if (input.dataset.optionPosition === '1' && jogger?.fit === 'petite' && !this.supportsPetite(values[0]?.value?.name)) {
      const regular = options[2].values.find((value) => {
        const size = this.parseSize(value.name);
        return size?.size === jogger.size && size.fit === 'regular';
      });
      const radio = regular && picker.querySelector(`input[data-option-position="3"][value="${regular.id}"]`);
      if (radio) {
        radio.checked = true;
        this.renderAdjustmentNotice(values[0].value.name, jogger.size);
        values = checked();
      }
    }
    if (values.every(({ radio, value }) => radio && value)) {
      this.nativePending = { ids: values.map(({ radio }) => radio.value), values: values.map(({ value }) => value.name) };
      this.updatePurchaseGuard();
    }
  }

  resolveAdjustments() {
    const bar = this.root.querySelector(SELECTOR_SELECTED_BAR);
    for (const [key, pending] of this.pendingAdjustments) {
      if (bar?.dataset.dealBarId !== pending.barId) {
        this.pendingAdjustments.delete(key);
        continue;
      }
      const row = bar.querySelectorAll(SELECTOR_ROW)[pending.rowIndex];
      if (!row) continue;
      const fields = this.getRowSelects(row);
      if (fields.color?.value !== pending.values[0] || fields.top?.value !== pending.values[1]) continue;
      if (fields.bottom?.value !== pending.values[2]) {
        const option = Array.from(fields.bottom?.options || []).find((option) => option.value === pending.values[2] && !option.disabled);
        if (option && !pending.dispatched) {
          pending.dispatched = true;
          fields.bottom.value = option.value;
          this.adjustingSelection = true;
          fields.bottom.dispatchEvent(new Event('change', { bubbles: true }));
          this.adjustingSelection = false;
        }
      }
      const rowState = this.getRowSelection(row).state;
      if (fields.bottom?.value === pending.values[2] && (this.kachingSelectionMatches() || rowState === 'soldout' || rowState === 'invalid')) {
        this.pendingAdjustments.delete(key);
        this.renderAdjustmentNotice(pending.values[0], pending.size);
      } else if (Date.now() - pending.startedAt > VARIANT_RESOLVE_TIMEOUT) {
        this.renderSelectionNotice(this.text('sizesError'));
      }
    }
    if (this.pendingAdjustments.size) this.scheduleInspection();
  }

  kachingSelectionMatches() {
    const rows = this.getSelectedRows();
    if (!rows.length || rows.some((row) => !row.variant)) return false;
    return quantitiesMatch(rows.map((row) => row.variant), this.variantQuantities);
  }

  getRowSelection(row) {
    const fields = this.getRowSelects(row);
    const values = [fields.color?.value, fields.top?.value, fields.bottom?.value];
    const top = this.parseSize(values[1]);
    const bottom = this.parseSize(values[2]);
    if (!top || !bottom || top.fit === 'petite' || (bottom.fit === 'petite' && !this.supportsPetite(values[0]))) {
      return { state: 'invalid', variant: null };
    }
    return resolveSelection(values, this.getWidgetProduct()?.variants);
  }

  getSelectedRows() {
    return Array.from(this.root.querySelectorAll(`${SELECTOR_SELECTED_BAR} ${SELECTOR_ROW}`), (row) => this.getRowSelection(row));
  }

  selectedVariants() {
    return this.isReady()
      ? this.getSelectedRows().map((row) => row.variant).filter(Boolean)
      : [this.getNativeVariant()].filter(Boolean);
  }

  isPreorder(variant) {
    const rule = this.preorderRule(variant?.options?.[0] ?? variant?.option1);
    if (!rule?.shipping?.trim()) return false;
    if (rule.mode === 'Always') return true;
    return rule.mode === 'When out of stock' && this.preorderInventory[String(variant?.id).split('/').pop()] === true;
  }

  preorderRule(color) {
    return this.copy.preorderRules?.[normalizeColor(color)];
  }

  missingInventory() {
    if (!this.inventoryUrl) return [];
    return [...new Set(this.selectedVariants().map((variant) => String(variant.id).split('/').pop()))]
      .filter((id) => typeof this.preorderInventory[id] !== 'boolean');
  }

  loadSelectedInventory() {
    // Liquid initially supplies 250 variants plus the native selection. Resolve
    // any other selected ID individually so large products aren't truncated.
    for (const id of this.missingInventory()) {
      if (this.inventoryRequests.has(id) || this.inventoryErrors.has(id)) continue;
      const url = new URL(this.inventoryUrl, window.location.origin);
      url.searchParams.set('view', 'performance-set-inventory');
      url.searchParams.set('variant', id);
      const requestAbort = new AbortController();
      const cancel = () => requestAbort.abort();
      this.abortController.signal.addEventListener('abort', cancel, { once: true });
      const timeout = window.setTimeout(cancel, 10000);
      const request = fetch(url, { signal: requestAbort.signal })
        .then(async (response) => {
          if (!response.ok) throw new Error('Inventory request failed');
          const data = await response.json();
          if (String(data.productId) !== this.root.dataset.performanceSetProductId || typeof data.variants?.[id] !== 'boolean') {
            throw new Error('Inventory response does not match the selected variant');
          }
          this.preorderInventory[id] = data.variants[id];
        })
        .catch(() => { this.inventoryErrors.add(id); })
        .finally(() => {
          window.clearTimeout(timeout);
          this.abortController.signal.removeEventListener('abort', cancel);
          this.inventoryRequests.delete(id);
          if (!this.abortController.signal.aborted && this.root.isConnected) {
            this.updatePurchaseGuard();
            this.scheduleInspection();
          }
        });
      this.inventoryRequests.set(id, request);
    }
  }

  renderPreorderNotice() {
    const variants = this.selectedVariants().filter((variant) => this.isPreorder(variant));
    this.root.querySelectorAll('[data-performance-set-standard-shipping]').forEach(node => { node.hidden = variants.length > 0; });
    let notice = this.root.querySelector('[data-performance-set-preorder]');
    if (!variants.length) {
      notice?.remove();
      return;
    }
    if (!notice) {
      notice = document.createElement('div');
      notice.dataset.performanceSetPreorder = '';
      notice.className = 'performance-set-kaching__preorder';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-atomic', 'true');
      notice.innerHTML = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg><div><strong></strong><p class="performance-set-kaching__preorder-intro"></p><ul></ul><p class="performance-set-kaching__preorder-shipping"></p></div>';
      this.root.querySelector('[data-block-type="buy-buttons"]')?.before(notice);
    }
    this.setText(notice.querySelector('strong'), this.text('preorderHeading'));
    this.setText(notice.querySelector('.performance-set-kaching__preorder-intro'), this.text(variants.length > 1 ? 'preorderIntroPlural' : 'preorderIntro'));
    const shipping = notice.querySelector('.performance-set-kaching__preorder-shipping');
    shipping.hidden = true;
    this.setText(shipping, '');
    const labels = [...new Set(variants.map((variant) => {
      const [color, top, bottom] = variant.options || [];
      return [color, top && `${this.text('topLabel')}: ${this.formatSize(top)}`, bottom && `${this.text('bottomLabel')}: ${this.formatSize(bottom)}`, this.preorderRule(color)?.shipping].filter(Boolean).join(' · ');
    }))];
    const list = notice.querySelector('ul');
    if (JSON.stringify(labels) !== list.dataset.labels) {
      list.replaceChildren(...labels.map((label) => {
        const item = document.createElement('li');
        item.textContent = label;
        return item;
      }));
      list.dataset.labels = JSON.stringify(labels);
    }
  }

  selectionState() {
    if (!this.isReady()) {
      const state = this.nativePending ? 'pending' : variantState(this.getNativeVariant());
      return state === 'available' && this.missingInventory().length ? 'pending' : state;
    }
    const rows = this.getSelectedRows();
    if (this.pendingAdjustments.size > 0) return 'pending';
    const state = packState(rows, { resolving: this.resolvingSelection, quantities: this.variantQuantities });
    return state === 'available' && this.missingInventory().length ? 'pending' : state;
  }

  selectionMessage(state = this.selectionState()) {
    if (state === 'soldout') return this.text('soldOutLabel');
    if (state === 'invalid') return this.text('combinationError') || this.text('unavailableLabel');
    if (this.missingInventory().some((id) => this.inventoryErrors.has(id))) return this.text('preorderError');
    return this.text('selectionPending');
  }

  purchaseNeedsResolution() {
    return this.selectionState() !== 'available';
  }

  updatePurchaseGuard() {
    this.loadSelectedInventory();
    this.renderPreorderNotice();
    const state = this.selectionState();
    const previousState = this.root.dataset.performanceSetSelectionState;
    this.root.dataset.performanceSetSelectionState = state;
    const disabled = state !== 'available';
    this.getSubmitButtons().forEach((button) => {
      if (!this.root.contains(button) && !this.boundSubmitButtons.has(button)) {
        // The mobile sticky button belongs to this form but lives outside root.
        this.boundSubmitButtons.add(button);
        this.mutationObserver.observe(button, {
          childList: true, characterData: true, subtree: true,
          attributes: true, attributeFilter: ['disabled', 'aria-busy'],
        });
      }
      const buttonDisabled = disabled || button.getAttribute('aria-busy') === 'true';
      if (button.disabled !== buttonDisabled) button.disabled = buttonDisabled;
      this.setAttribute(button, 'aria-disabled', String(buttonDisabled));
      if (button.classList.contains('button--subdued') !== disabled) button.classList.toggle('button--subdued', disabled);
    });
    this.syncBuyButtonLabel(state);
    if (state !== previousState || (disabled && this.missingInventory().some((id) => this.inventoryErrors.has(id)))) {
      if (disabled) this.renderSelectionNotice(this.selectionMessage(state));
      else this.removeSelectionNotice();
    }
  }

  renderAdjustmentNotice(color, size) {
    this.renderSelectionNotice(this.text('adjustment', { color, size, regular: this.text('regularLabel'), petite: this.text('petiteLabel') }));
  }

  renderSelectionNotice(message) {
    if (!message.trim()) {
      this.removeSelectionNotice();
      return;
    }
    let notice = this.root.querySelector('[data-performance-set-selection-notice]');
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.performanceSetSelectionNotice = '';
      notice.className = 'performance-set-kaching__status';
      notice.setAttribute('role', 'status');
      this.root.querySelector('[data-block-type="buy-buttons"]')?.before(notice);
    }
    this.setText(notice, message);
  }

  removeSelectionNotice() {
    this.root.querySelector('[data-performance-set-selection-notice]')?.remove();
  }

  decorateDealBars(dealBars) {
    dealBars.forEach((bar, index) => {
      const quantity = this.getBarQuantity(bar, index);
      this.setAttribute(bar, 'data-performance-set-quantity', String(quantity));
      this.decorateTierHeader(bar, quantity);

      const rows = Array.from(bar.querySelectorAll(SELECTOR_ROW));
      rows.forEach((row, rowIndex) => this.decorateSelectorRow(row, rowIndex, bar, quantity));
    });

    this.root.classList.add('is-kaching-presented');
  }

  decorateTierHeader(bar, quantity) {
    const pricing = bar.querySelector('.kaching-bundles__bar-pricing');
    if (!pricing) return;

    let compare = pricing.querySelector(':scope > .performance-set-kaching__tier-compare');
    if (!compare) {
      compare = document.createElement('del');
      compare.className = 'performance-set-kaching__tier-compare';
      pricing.prepend(compare);
    }

    const compareText = this.root.getAttribute(`data-performance-set-compare-total-${quantity}`) || '';
    this.setText(compare, compareText);
    compare.hidden = !compareText || this.isZeroMoney(compareText);
  }

  decorateSelectorRow(row, rowIndex, bar, quantity) {
    const content = row.querySelector('.kaching-bundles__bar-variant__content');
    const main = row.querySelector('.kaching-bundles__bar-variant-main');
    const selectsContainer = row.querySelector('.kaching-bundles__bar-variant-selects');
    if (!content || !main || !selectsContainer) return;
    const selection = this.getRowSelection(row);
    const rowState = selection.state;
    const components = this.getComponentAvailability(this.getRowSelects(row).color?.value, bar.matches(SELECTOR_SELECTED_BAR));
    this.setAttribute(row, 'data-performance-set-selection-state', rowState);

    let number = content.querySelector(':scope > .performance-set-kaching__line-number');
    if (!number) {
      number = document.createElement('span');
      number.className = 'performance-set-kaching__line-number';
      number.setAttribute('aria-hidden', 'true');
      content.prepend(number);
    }
    this.setText(number, String(rowIndex + 1));
    if (content.querySelector('.kaching-bundles__bar-variant-image')) {
      let preview = content.querySelector('.performance-set-kaching__image-preview');
      if (!preview) {
        preview = document.createElement('button');
        preview.type = 'button';
        preview.className = 'performance-set-kaching__image-preview';
        preview.setAttribute('aria-haspopup', 'dialog');
        preview.innerHTML = '<span aria-hidden="true"><svg width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" stroke-width="1.5"/><path d="m13 13 4 4M6 8.5h5M8.5 6v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>';
        content.appendChild(preview);
      }
      this.setAttribute(preview, 'aria-label', `Preview ${this.getRowSelects(row).color?.value || 'product'} image`);
    }

    let titleRow = main.querySelector(':scope > .performance-set-kaching__line-title-row');
    if (!titleRow) {
      titleRow = document.createElement('div');
      titleRow.className = 'performance-set-kaching__line-title-row';
      titleRow.innerHTML = `
        <p class="performance-set-kaching__line-title"></p>
        <span class="performance-set-kaching__line-price">
          <del class="performance-set-kaching__line-compare"></del>
          <strong class="performance-set-kaching__line-current"></strong>
          <span class="performance-set-kaching__line-suffix"></span>
        </span>
      `;
      main.prepend(titleRow);
    }

    this.setText(
      titleRow.querySelector('.performance-set-kaching__line-title'),
      this.text('itemTitle')
    );
    this.setText(titleRow.querySelector('.performance-set-kaching__line-suffix'), this.text('priceSuffix'));
    const lineCompare = titleRow.querySelector('.performance-set-kaching__line-compare');
    const lineCompareText = this.root.dataset.performanceSetComparePrice || '';
    this.setText(lineCompare, lineCompareText);
    lineCompare.hidden = !lineCompareText || this.isZeroMoney(lineCompareText);

    const totalPrice = bar.querySelector('.kaching-bundles__bar-price')?.textContent.trim() || '';
    this.setText(
      titleRow.querySelector('.performance-set-kaching__line-current'),
      this.divideFormattedMoney(totalPrice, quantity)
    );

    // Availability belongs to the selected top/jogger combination, not the top.
    let stockStatus = main.querySelector(':scope > .performance-set-kaching__row-stock-status');
    if (rowState === 'soldout' || rowState === 'invalid') {
      if (!stockStatus) {
        stockStatus = document.createElement('p');
        stockStatus.className = 'performance-set-kaching__row-stock-status';
        stockStatus.setAttribute('role', 'status');
        main.appendChild(stockStatus);
      }
      this.setText(stockStatus, [this.text('itemTitle'), this.text(rowState === 'soldout' ? 'soldOutLabel' : 'unavailableLabel')].filter(Boolean).join(' — '));
    } else {
      stockStatus?.remove();
    }

    const selects = Array.from(selectsContainer.querySelectorAll(`:scope > ${SELECTOR_SELECT}`));
    selects.forEach((select, selectIndex) => {
      const fieldType = this.getFieldType(select, selectIndex);
      select.classList.add('performance-set-kaching__field-select', `performance-set-kaching__field-select--${fieldType}`);

      let label = selectsContainer.querySelector(`:scope > .performance-set-kaching__field-label--${fieldType}`);
      if (!label) {
        label = document.createElement('span');
        label.className = `performance-set-kaching__field-label performance-set-kaching__field-label--${fieldType}`;
        selectsContainer.appendChild(label);
      }

      const optionName = this.optionName(fieldType) || select.dataset.name || select.getAttribute('aria-label') || '';
      this.decorateFieldLabel(
        label,
        `${fieldType === 'color' ? `#${rowIndex + 1} ${optionName}` : optionName}:`,
        fieldType,
        rowState,
        this.isPreorder(selection.variant)
      );

      if (fieldType === 'color') this.decorateColorSelect(select, selectsContainer);
      if (fieldType === 'top' || fieldType === 'bottom') {
        const originalOptions = Array.from(select.options);
        const sortedOptions = [...originalOptions].sort((a, b) => compareScrubSizes(a.value, b.value));
        // Keep the same option nodes and selected value: Kaching owns variant IDs.
        if (sortedOptions.some((option, index) => option !== originalOptions[index])) {
          const selectedValue = select.value;
          sortedOptions.forEach((option) => select.appendChild(option));
          select.value = selectedValue;
        }
        sortedOptions.forEach((option) => {
          const component = components?.[fieldType]?.get(option.value);
          if (component) {
            // Shopify's component availability includes its continue-selling
            // policy. Keep other garment sizes independent of this selection.
            if (option.disabled !== !component.available) option.disabled = !component.available;
            option.dataset.componentStockDisabled = String(!component.available);
          } else if (option.dataset.componentStockDisabled === 'true') {
            // A colour change must not inherit the previous colour's stock.
            option.disabled = false;
            delete option.dataset.componentStockDisabled;
          }
          this.setText(option, [this.formatSize(option.value), component?.available === false ? this.text('soldOutLabel') : ''].filter(Boolean).join(' · '));
        });
      }
    });
  }

  decorateFieldLabel(label, text, fieldType, rowState, preorder = false) {
    let labelText = label.querySelector(':scope > .performance-set-kaching__field-label-text');
    if (!labelText) {
      labelText = document.createElement('span');
      labelText.className = 'performance-set-kaching__field-label-text';
      label.replaceChildren(labelText);
    }
    this.setText(labelText, text);

    let lowStock = label.querySelector(':scope > .performance-set-kaching__fake-low-stock');
    if (fieldType !== 'top' || preorder || rowState !== 'available' || this.fakeLowStockCount == null || !this.text('lowStock').trim()) {
      lowStock?.remove();
      return;
    }

    if (!lowStock) {
      lowStock = document.createElement('span');
      lowStock.className = 'performance-set-kaching__fake-low-stock';
      label.appendChild(lowStock);
    }
    this.setText(lowStock, this.text('lowStock', { count: this.fakeLowStockCount }));
  }

  decorateColorSelect(select, selectsContainer) {
    const row = select.closest(SELECTOR_ROW);
    Array.from(select.options).forEach((option) => {
      // Option values are Kaching's canonical product names. Text can already
      // contain app-generated stock suffixes or decoration from a prior render.
      const baseLabel = option.value;
      const presentation = this.colorPresentation(baseLabel, row);
      this.setText(option, presentation.label);
    });

    let swatch = selectsContainer.querySelector(':scope > .performance-set-kaching__color-swatch');
    if (!swatch) {
      swatch = document.createElement('span');
      swatch.className = 'performance-set-kaching__color-swatch';
      swatch.setAttribute('aria-hidden', 'true');
      selectsContainer.appendChild(swatch);
    }

    const selectedColor = select.value;
    swatch.style.background = this.swatchBackgrounds[normalizeColor(selectedColor)] || normalizeColor(selectedColor);

    const label = selectsContainer.querySelector('.performance-set-kaching__field-label--color');
    const preorder = this.isPreorder(this.getRowSelection(row).variant);
    const badgeText = preorder ? this.text('preorderLabel') : this.colorPresentation(selectedColor, row).isNew ? this.text('newLabel') : '';
    let badge = label?.querySelector('.performance-set-kaching__new-badge');
    if (!label || !badgeText.trim()) {
      badge?.remove();
    } else {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'performance-set-kaching__new-badge';
        label.appendChild(badge);
      }
      badge.classList.toggle('performance-set-kaching__new-badge--preorder', preorder);
      this.setText(badge, badgeText);
    }
  }

  colorPresentation(color, row) {
    return colorPresentation(color, colorAvailability(color, this.getWidgetProduct()?.variants), {
      coreColors: this.coreColors,
      limitedColors: this.limitedColors,
      copy: this.copy,
      // A colour can contain both stocked and backordered sizes. Only an
      // Always rule labels the whole dropdown option; the row badge is exact.
      preorder: this.preorderRule(color)?.mode === 'Always' && Boolean(this.preorderRule(color)?.shipping?.trim()),
    });
  }

  getBarQuantity(bar, index) {
    const rowCount = bar.querySelectorAll(SELECTOR_ROW).length;
    if (rowCount > 0) return rowCount;

    const titleQuantity = parseInt(bar.querySelector('.kaching-bundles__bar-title')?.textContent, 10);
    return Number.isFinite(titleQuantity) && titleQuantity > 0 ? titleQuantity : index + 1;
  }

  getFieldType(select, index) {
    const name = `${select.dataset.name || ''} ${select.getAttribute('aria-label') || ''}`.toLowerCase();
    if (name.includes('color') || name.includes('colour')) return 'color';
    if (name.includes('top')) return 'top';
    if (name.includes('bottom') || name.includes('jogger')) return 'bottom';
    return ['color', 'top', 'bottom'][index] || `option-${index + 1}`;
  }

  optionName(fieldType) {
    return this.text({ color: 'colorLabel', top: 'topLabel', bottom: 'bottomLabel' }[fieldType]);
  }

  formatSize(value) {
    const parsed = this.parseSize(value);
    if (!parsed) return value;
    const fit = this.text(parsed.fit === 'petite' ? 'petiteLabel' : 'regularLabel');
    // When an override is cleared, preserve the product's own display label.
    return fit ? `${parsed.size} ${fit}` : String(value).replace(/^(Top|Jogger)\s+/i, '').replace(' - ', ' ');
  }

  text(key, values = {}) {
    // Substitute once so inserted product names and merchant copy remain literal.
    return String(this.copy[key] ?? '').replace(/\{(\w+)\}/g, (token, name) => (
      Object.hasOwn(values, name) ? String(values[name]) : token
    ));
  }

  readPromotionalColors(key) {
    return new Set(this.text(key).split(',').map((value) => value.trim().replace(/\s+/g, ' ').toLowerCase()).filter(Boolean));
  }

  divideFormattedMoney(formattedMoney, quantity) {
    if (!formattedMoney || quantity <= 1) return formattedMoney;

    const amountMatch = formattedMoney.match(/\d[\d\s.,'’]*/);
    if (!amountMatch) return formattedMoney;

    const rawAmount = amountMatch[0].trim();
    const lastComma = rawAmount.lastIndexOf(',');
    const lastPeriod = rawAmount.lastIndexOf('.');
    const possibleSeparatorIndex = Math.max(lastComma, lastPeriod);
    const trailingDigits = possibleSeparatorIndex >= 0
      ? rawAmount.slice(possibleSeparatorIndex + 1).replace(/\D/g, '').length
      : 0;
    const decimalSeparator = possibleSeparatorIndex >= 0 && trailingDigits > 0 && trailingDigits <= 2
      ? rawAmount[possibleSeparatorIndex]
      : '';
    const fractionDigits = decimalSeparator ? trailingDigits : 0;
    const normalizedAmount = decimalSeparator
      ? rawAmount
        .replace(new RegExp(`[^\\d\\${decimalSeparator}]`, 'g'), '')
        .replace(decimalSeparator, '.')
      : rawAmount.replace(/\D/g, '');
    const numericAmount = Number(normalizedAmount);
    if (!Number.isFinite(numericAmount)) return formattedMoney;

    const divided = (numericAmount / quantity).toFixed(fractionDigits);
    const localized = decimalSeparator === ',' ? divided.replace('.', ',') : divided;
    const start = amountMatch.index;
    const end = start + amountMatch[0].length;
    return `${formattedMoney.slice(0, start)}${localized}${formattedMoney.slice(end)}`;
  }

  isZeroMoney(formattedMoney) {
    const digits = formattedMoney.replace(/\D/g, '');
    return digits !== '' && Number(digits) === 0;
  }

  getHeadlinePriceNode() {
    const headline = this.root.querySelector('.product-info__price sale-price');
    // Update only the amount, preserving the theme's screen-reader label.
    return Array.from(headline?.childNodes || []).find((node) => (
      node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    ));
  }

  syncHeadlinePrice() {
    // The headline represents one set even when a larger pack is selected.
    const singleSet = this.root.querySelector(`${DEAL_BAR_SELECTOR}[data-performance-set-quantity="1"]`);
    const price = singleSet?.querySelector('.kaching-bundles__bar-price')?.textContent.trim();
    const amount = this.getHeadlinePriceNode();
    if (!amount) return;
    if (!price) {
      this.restoreHeadlinePrice();
      return;
    }

    if (!this.originalHeadlinePrices.has(amount)) {
      this.originalHeadlinePrices.set(amount, amount.textContent);
    }
    this.setText(amount, price);
  }

  restoreHeadlinePrice() {
    const amount = this.getHeadlinePriceNode();
    const originalPrice = amount ? this.originalHeadlinePrices.get(amount) : null;
    if (originalPrice != null) this.setText(amount, originalPrice);
  }

  syncBuyButtonLabel(state = this.selectionState()) {
    const selectedBar = this.root.querySelector(SELECTOR_SELECTED_BAR);
    const price = this.isReady()
      ? selectedBar?.querySelector('.kaching-bundles__bar-price')?.textContent.trim()
      : this.getHeadlinePriceNode()?.textContent.trim();
    const message = state === 'pending'
      ? this.text('pendingLabel') || this.text('loading') || this.text('unavailableLabel')
      : this.text({ invalid: 'unavailableLabel', soldout: 'soldOutLabel' }[state]);
    const variants = this.selectedVariants();
    const allPreorder = variants.length > 0 && variants.every((variant) => this.isPreorder(variant));
    const purchaseLabel = this.text(allPreorder ? 'preorderLabel' : 'addLabel');
    this.getSubmitButtons().forEach((button) => {
      const label = this.getBuyButtonLabel(button);
      if (!label) return;
      if (state === 'available' && price) this.syncBuyButtonLabelParts(label, purchaseLabel, price);
      else this.setText(label, state === 'available' ? purchaseLabel : message);
    });
    const quantity = selectedBar?.getAttribute('data-performance-set-quantity');
    if (state === 'available' && price) this.syncHeadlinePriceSuffix(price, quantity);
    if (quantity) this.root.dataset.kachingSelectedQuantity = quantity;
  }

  syncHeadlinePriceSuffix(price, quantity) {
    // Mirror the selected bundle total in the headline price, like the buy button does.
    this.root.querySelectorAll('.product-info__price [data-performance-set-add-price-suffix]').forEach((node) => {
      this.setText(node, price);
    });
    if (quantity) this.syncHeadlineComparePrice(quantity, price);
  }

  syncHeadlineComparePrice(quantity, price) {
    const priceList = this.root.querySelector('.product-info__price price-list');
    const salePrice = priceList?.querySelector('sale-price');
    if (!salePrice) return;

    let amount = priceList.querySelector('[data-performance-set-compare-price]');
    if (!amount) {
      // The theme omits compare-at-price when the single set isn't discounted.
      const compare = document.createElement('compare-at-price');
      compare.className = 'text-subdued line-through';
      amount = document.createElement('span');
      amount.dataset.performanceSetComparePrice = '';
      compare.append(amount);
      salePrice.after(compare);
    }

    const compareText = this.root.getAttribute(`data-performance-set-compare-total-${quantity}`) || '';
    const visible = Boolean(compareText) && !this.isZeroMoney(compareText);
    this.setText(amount, compareText);
    amount.closest('compare-at-price').hidden = !visible;
    salePrice.classList.toggle('text-on-sale', visible);
    this.syncSavingsBadge(visible ? compareText : '', price);
  }

  syncSavingsBadge(compareText, price) {
    const badge = this.root.querySelector('.product-info__price [data-performance-set-savings-badge]');
    if (!badge) return;

    // Both amounts come from the same money format, so their digits share a scale.
    const savings = this.moneyDigits(compareText) - this.moneyDigits(price);
    const label = this.root.dataset.performanceSetSavingsLabel || '';
    const visible = Boolean(compareText) && savings > 0 && label.includes('[savings]');
    if (visible) this.setText(badge, label.replace('[savings]', this.formatMoney(savings)));
    badge.hidden = !visible;
  }

  moneyDigits(formattedMoney) {
    return Number(formattedMoney.replace(/\D/g, '')) || 0;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(window.Shopify?.locale, {
        style: 'currency',
        currency: window.Shopify?.currency?.active,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  syncBuyButtonLabelParts(label, addLabel, price) {
    let addText = label.querySelector(':scope > [data-performance-set-add-text]');
    let separator = label.querySelector(':scope > [data-performance-set-add-separator]');
    let priceText = label.querySelector(':scope > [data-performance-set-add-price]');

    if (!addText || !separator || !priceText) {
      addText = document.createElement('span');
      addText.dataset.performanceSetAddText = '';

      separator = document.createElement('span');
      separator.dataset.performanceSetAddSeparator = '';

      priceText = document.createElement('span');
      priceText.dataset.performanceSetAddPrice = '';

      label.replaceChildren(addText, separator, priceText);
    }

    this.setText(addText, addLabel);
    this.setText(separator, ' - ');
    this.setText(priceText, price);
  }

  getSubmitButtons() {
    const form = this.getProductForm();
    return form ? Array.from(form.elements).filter((element) => element.matches('button[type="submit"]'))
      : Array.from(this.root.querySelectorAll('.product-info__buy-buttons button[type="submit"]'));
  }

  getBuyButtonLabel(button) {
    const iconLabel = button?.querySelector('.text-with-icon > span:last-child');
    return iconLabel || button?.contentElement || button || null;
  }

  getLoadingGraceDelay() {
    const delay = Number.parseInt(this.root.dataset.kachingLoadingGraceMs, 10);
    return Number.isFinite(delay) && delay > 0 ? delay : 0;
  }

  getFallbackDelay() {
    return this.getLoadingGraceDelay() || FALLBACK_DELAY;
  }

  readFakeLowStockCount() {
    const count = Number.parseInt(this.root.dataset.performanceSetFakeLowStockCount, 10);
    return Number.isFinite(count) && count > 0 ? count : null;
  }

  readColorList(datasetKey) {
    return new Set(
      (this.root.dataset[datasetKey] || '')
        .split('|')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
    );
  }

  setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  getProductForm() {
    const picker = this.root.querySelector('variant-picker[form-id]');
    const formId = picker?.getAttribute('form-id');
    return formId ? document.getElementById(formId) : null;
  }

  renderStatusMessage(message) {
    if (!message.trim()) {
      this.removeStatusMessage();
      return;
    }
    let status = this.root.querySelector('[data-performance-set-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'performance-set-kaching__status';
      status.dataset.performanceSetStatus = '';
      status.setAttribute('role', 'status');

      const buyButtons = this.root.querySelector('[data-block-type="buy-buttons"]');
      buyButtons?.before(status);
    }

    status.textContent = message;
  }

  removeStatusMessage() {
    this.root.querySelector('[data-performance-set-status]')?.remove();
  }
}

function initialize(root) {
  if (!(root instanceof HTMLElement) || controllers.has(root)) return;

  let copy;
  try {
    copy = JSON.parse(root.dataset.performanceSetCopy);
    if (!copy || typeof copy !== 'object' || Array.isArray(copy)) throw new Error('Invalid copy configuration');
  } catch (_error) {
    root.classList.remove('is-kaching-loading');
    return;
  }

  const controller = new PerformanceSetKachingConnector(root, copy);
  controllers.set(root, controller);
  controller.connect();
}

function scan(scope = document) {
  if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initialize(scope);
  scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
}

scan();

const pageObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) scan(node);
    });
  }
});

pageObserver.observe(document.documentElement, { childList: true, subtree: true });
